/**
 * Disclosure Policy Matrix
 * 
 * Defines EXACTLY what is shown at each disclosure level.
 * Separates DATA PROVENANCE (safe) from MODEL IP (protected).
 * 
 * Principle: Explain the WHY and WHAT TO DO NEXT, not the scoring function.
 * 
 * @see WORKPLAN-LENDER-AUDIT-TRACEABILITY-MATRIX.md
 */

import { DisclosureLevel } from '@/types/evidenceContract';

// =============================================================================
// DISCLOSURE LEVEL DEFINITIONS
// =============================================================================

/**
 * MEMBER (Level 1) - "Explain"
 * High trust, low IP leakage. User sees their own data.
 * 
 * SHOW:
 * - Metric value + time window + as-of
 * - Included/excluded sources for THEIR OWN data
 * - Plain-English rule summaries
 * - Factor direction + contribution band (Low/Medium/High)
 * - Coaching tips
 * 
 * HIDE:
 * - Exact weights
 * - Exact thresholds / scoring curves
 * - Full feature list
 * - Other users' data
 */

/**
 * LENDER (Level 2) - "Underwriting Evidence"
 * Audit-friendly, still protects IP. Supports credit decisions.
 * 
 * SHOW:
 * - Everything in MEMBER
 * - DTI components, income sources, expense categories
 * - Reason codes + adverse action reasons (FCRA requirement)
 * - Factor breakdown as ranges/ranks/normalized indexes
 * - Aggregate transaction counts (not full list)
 * - Liability details relevant to underwriting
 * 
 * HIDE:
 * - Full scoring equation
 * - Feature engineering internals
 * - Weights/curves (unless under NDA)
 * - Raw transaction-level detail (show aggregates)
 */

/**
 * AUDITOR (Level 3) - "Full Audit Pack"
 * Restricted, privileged access. Prove exact reproducibility.
 * 
 * SHOW:
 * - Everything in LENDER
 * - run_id, algo_version, git_sha, config_version
 * - Input/output hashes for integrity verification
 * - Full feature table
 * - Validation results (stored vs calculated)
 * - Full evidence links (transaction IDs)
 * - Exact scoring curves/weights (under NDA only)
 */

// =============================================================================
// TAB VISIBILITY MATRIX
// =============================================================================

export type TabKey = 'summary' | 'breakdown' | 'calculation' | 'sources' | 'raw';

/**
 * Tab visibility by disclosure level
 *
 * TODO: RBAC - Re-enable tiered access when role-based security is implemented
 * For now, all levels see all tabs during development/testing
 */
export const TAB_VISIBILITY: Record<DisclosureLevel, TabKey[]> = {
  // TODO: RBAC - Restore these restrictions when RBAC is implemented:
  // MEMBER: ['summary'],
  // LENDER: ['summary', 'breakdown', 'calculation'],
  // AUDITOR: ['summary', 'breakdown', 'calculation', 'sources'],

  // TEMPORARY: All levels see all tabs until RBAC is implemented
  MEMBER: ['summary', 'breakdown', 'calculation', 'sources'],
  LENDER: ['summary', 'breakdown', 'calculation', 'sources'],
  AUDITOR: ['summary', 'breakdown', 'calculation', 'sources'],
};

// =============================================================================
// FIELD-LEVEL REDACTION RULES
// =============================================================================

export interface FieldRedactionRule {
  field: string;
  member: 'SHOW' | 'HIDE' | 'COARSEN';
  lender: 'SHOW' | 'HIDE' | 'COARSEN';
  auditor: 'SHOW';
  coarsen_strategy?: 'BAND' | 'RANGE' | 'AGGREGATE' | 'MASK';
}

export const FIELD_REDACTION_RULES: FieldRedactionRule[] = [
  // === SUMMARY FIELDS (mostly safe) ===
  { field: 'value', member: 'SHOW', lender: 'SHOW', auditor: 'SHOW' },
  { field: 'unit', member: 'SHOW', lender: 'SHOW', auditor: 'SHOW' },
  { field: 'period', member: 'SHOW', lender: 'SHOW', auditor: 'SHOW' },
  { field: 'as_of', member: 'SHOW', lender: 'SHOW', auditor: 'SHOW' },
  { field: 'summary.one_liner', member: 'SHOW', lender: 'SHOW', auditor: 'SHOW' },
  { field: 'summary.trend', member: 'SHOW', lender: 'SHOW', auditor: 'SHOW' },
  { field: 'summary.coaching_tip', member: 'SHOW', lender: 'SHOW', auditor: 'SHOW' },
  
  // === COMPONENT BREAKDOWN (provenance - safe with aggregation) ===
  { field: 'components.count', member: 'HIDE', lender: 'SHOW', auditor: 'SHOW' },
  { field: 'components.items', member: 'HIDE', lender: 'COARSEN', auditor: 'SHOW', coarsen_strategy: 'AGGREGATE' },
  { field: 'components.items[].percentage', member: 'HIDE', lender: 'SHOW', auditor: 'SHOW' },
  { field: 'components.items[].value', member: 'HIDE', lender: 'COARSEN', auditor: 'SHOW', coarsen_strategy: 'RANGE' },
  
  // === CALCULATION (MODEL IP - protect!) ===
  { field: 'calculation.formula', member: 'HIDE', lender: 'HIDE', auditor: 'SHOW' },
  { field: 'calculation.steps', member: 'HIDE', lender: 'HIDE', auditor: 'SHOW' },
  { field: 'calculation.weights', member: 'HIDE', lender: 'HIDE', auditor: 'SHOW' },  // NDA ONLY
  { field: 'calculation.thresholds', member: 'HIDE', lender: 'HIDE', auditor: 'SHOW' },  // NDA ONLY
  { field: 'calculation.config_ref', member: 'HIDE', lender: 'HIDE', auditor: 'SHOW' },
  
  // === SOURCES (provenance - safe at appropriate granularity) ===
  { field: 'sources.accounts', member: 'COARSEN', lender: 'COARSEN', auditor: 'SHOW', coarsen_strategy: 'MASK' },
  { field: 'sources.liabilities', member: 'HIDE', lender: 'SHOW', auditor: 'SHOW' },
  { field: 'sources.transactions.included', member: 'HIDE', lender: 'HIDE', auditor: 'SHOW' },
  { field: 'sources.transactions.excluded', member: 'HIDE', lender: 'HIDE', auditor: 'SHOW' },
  { field: 'sources.transactions.included_count', member: 'HIDE', lender: 'SHOW', auditor: 'SHOW' },
  { field: 'sources.bureau', member: 'HIDE', lender: 'COARSEN', auditor: 'SHOW', coarsen_strategy: 'AGGREGATE' },
  
  // === INTEGRITY (audit only) ===
  { field: 'integrity', member: 'HIDE', lender: 'HIDE', auditor: 'SHOW' },
  { field: 'run_id', member: 'HIDE', lender: 'SHOW', auditor: 'SHOW' },
  { field: 'algo_version', member: 'HIDE', lender: 'SHOW', auditor: 'SHOW' },
  { field: 'git_sha', member: 'HIDE', lender: 'HIDE', auditor: 'SHOW' },
];

// =============================================================================
// METRIC-SPECIFIC DISCLOSURE OVERRIDES
// =============================================================================

/**
 * Some metrics have special disclosure rules based on regulatory requirements
 * or business sensitivity.
 */
export interface MetricDisclosureOverride {
  metric_key: string;
  reason: string;
  overrides: Partial<Record<DisclosureLevel, {
    allowed_tabs?: TabKey[];
    coarsen_value?: boolean;
    hide_components?: boolean;
  }>>;
}

export const METRIC_OVERRIDES: MetricDisclosureOverride[] = [
  // BUG-UI-032: VeraScore should have Calculation tab showing weighted sum formula
  // BUG-UI-068: Added Sources tab to show contributing factors with links to factor modals
  // VeraScore = Σ(factor_score × weight) - this is auditable information
  {
    metric_key: 'verascore_current',
    reason: 'Core scoring output - calculation shows factor weights (auditable)',
    overrides: {
      MEMBER: { allowed_tabs: ['summary'], coarsen_value: false },
      LENDER: { allowed_tabs: ['summary', 'breakdown', 'calculation', 'sources'], hide_components: false },
    },
  },
  // Credit score - bureau data, standard disclosure
  {
    metric_key: 'credit_score_as_of',
    reason: 'Bureau data - standard FCRA disclosure applies',
    overrides: {
      MEMBER: { allowed_tabs: ['summary'] },
      LENDER: { allowed_tabs: ['summary', 'sources'] },
    },
  },
  // Factor scores - allow calculation tab to show how factor score is derived
  {
    metric_key: 'factor_income_stability',
    reason: 'Proprietary factor scoring - calculation shows curve lookup',
    overrides: {
      MEMBER: { allowed_tabs: ['summary'], coarsen_value: true },
      LENDER: { allowed_tabs: ['summary', 'breakdown', 'calculation'], hide_components: false },
    },
  },
];

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get visible tabs for a metric at a given disclosure level.
 */
export function getVisibleTabsForMetric(
  metricKey: string,
  level: DisclosureLevel
): TabKey[] {
  // Check for metric-specific override
  const override = METRIC_OVERRIDES.find(m => m.metric_key === metricKey);
  if (override?.overrides[level]?.allowed_tabs) {
    return override.overrides[level]!.allowed_tabs!;
  }
  // Fall back to default tab visibility
  return TAB_VISIBILITY[level];
}

/**
 * Check if a field should be shown at a given disclosure level.
 */
export function shouldShowField(
  fieldPath: string,
  level: DisclosureLevel
): 'SHOW' | 'HIDE' | 'COARSEN' {
  const rule = FIELD_REDACTION_RULES.find(r => r.field === fieldPath);
  if (!rule) return 'HIDE'; // Default to hiding unknown fields

  switch (level) {
    case 'MEMBER': return rule.member;
    case 'LENDER': return rule.lender;
    case 'AUDITOR': return rule.auditor;
    default: return 'HIDE';
  }
}

/**
 * Get the coarsening strategy for a field.
 */
export function getCoarsenStrategy(fieldPath: string): FieldRedactionRule['coarsen_strategy'] {
  const rule = FIELD_REDACTION_RULES.find(r => r.field === fieldPath);
  return rule?.coarsen_strategy;
}

// =============================================================================
// CONTRIBUTION BAND MAPPING (replaces exact weights for MEMBER/LENDER)
// =============================================================================

export type ContributionBand = 'VERY_LOW' | 'LOW' | 'MODERATE' | 'HIGH' | 'VERY_HIGH';

/**
 * Convert a numeric contribution (0-1) to a human-friendly band.
 * This is shown instead of exact weights.
 */
export function toContributionBand(contribution: number): ContributionBand {
  if (contribution < 0.1) return 'VERY_LOW';
  if (contribution < 0.25) return 'LOW';
  if (contribution < 0.5) return 'MODERATE';
  if (contribution < 0.75) return 'HIGH';
  return 'VERY_HIGH';
}

/**
 * Get human-friendly description for contribution band.
 */
export function getContributionDescription(band: ContributionBand): string {
  const descriptions: Record<ContributionBand, string> = {
    VERY_LOW: 'had minimal impact',
    LOW: 'had a small impact',
    MODERATE: 'had a moderate impact',
    HIGH: 'had a significant impact',
    VERY_HIGH: 'was a major factor',
  };
  return descriptions[band];
}

